// ─── Queue Copilot — communication Architecture Explorer → Copilot ─────────
// Double stockage : localStorage (réactivité UI) + fichier arch-queue.json via /arch-queue
// Cycle de vie : pending → read → done
// Copilot modifie arch-queue.json → syncFromFile() au montage resync le localStorage

export interface CopilotQueueItem {
  id: string;
  timestamp: string;
  status: 'pending' | 'read' | 'done';
  nodeId: string;
  nodeLabel: string;
  nodeLayer: string;
  nodeRole: string;
  keyFiles: string[];
  question: string;
}

const STORAGE_KEY = 'arch_copilot_queue';
const MAX_DONE_KEPT = 3;
const API_ENDPOINT = '/arch-queue';

// ─── Lecture depuis localStorage (synchrone, pour l'UI) ───────────────────
export function readQueue(): CopilotQueueItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CopilotQueueItem[]) : [];
  } catch {
    return [];
  }
}

// ─── Écriture : localStorage + fichier disque ─────────────────────────────
function writeQueue(queue: CopilotQueueItem[]): void {
  const active = queue.filter(i => i.status !== 'done');
  const done = queue.filter(i => i.status === 'done').slice(-MAX_DONE_KEPT);
  const final = [...active, ...done];
  localStorage.setItem(STORAGE_KEY, JSON.stringify(final));
  window.dispatchEvent(new Event('arch_queue_update'));
  // Sync vers fichier disque (pour lecture par Copilot)
  fetch(API_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(final),
  }).catch(() => {/* silencieux si serveur dev non disponible */});
}

// ─── Sync depuis fichier → localStorage (appelé au montage) ───────────────
// Permet à Copilot de marquer des messages done/read en modifiant arch-queue.json
export async function syncFromFile(): Promise<void> {
  try {
    const res = await fetch(API_ENDPOINT);
    if (!res.ok) return;
    const fileQueue = (await res.json()) as CopilotQueueItem[];
    if (!Array.isArray(fileQueue)) return;
    // Fusionner : le fichier fait autorité pour les statuts (Copilot peut les mettre à jour)
    const localQueue = readQueue();
    const merged = localQueue.map(local => {
      const fromFile = fileQueue.find(f => f.id === local.id);
      // Si le fichier a un statut plus avancé, on l'applique
      if (fromFile && statusRank(fromFile.status) > statusRank(local.status)) {
        return { ...local, status: fromFile.status };
      }
      return local;
    });
    // Ajouter les items du fichier qui ne sont pas en local (cas edge)
    const localIds = new Set(localQueue.map(i => i.id));
    for (const fileItem of fileQueue) {
      if (!localIds.has(fileItem.id)) merged.push(fileItem);
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    window.dispatchEvent(new Event('arch_queue_update'));
  } catch {
    // silencieux si endpoint non disponible (prod)
  }
}

function statusRank(s: CopilotQueueItem['status']): number {
  return s === 'pending' ? 0 : s === 'read' ? 1 : 2;
}

// ─── Ajout d'un message ───────────────────────────────────────────────────
export function addToQueue(
  item: Omit<CopilotQueueItem, 'id' | 'timestamp' | 'status'>
): CopilotQueueItem {
  const newItem: CopilotQueueItem = {
    ...item,
    id: Math.random().toString(36).slice(2, 8),
    timestamp: new Date().toISOString(),
    status: 'pending',
  };
  writeQueue([...readQueue(), newItem]);
  return newItem;
}

// ─── Annulation d'un message pending ─────────────────────────────────────
export function cancelQueueItem(id: string): void {
  writeQueue(readQueue().filter(i => i.id !== id));
}

// ─── Récupère le message actif (pending ou read) d'un nœud ───────────────
export function getNodeQueueItem(nodeId: string): CopilotQueueItem | null {
  return (
    readQueue()
      .filter(i => i.nodeId === nodeId && i.status !== 'done')
      .sort((a, b) => b.timestamp.localeCompare(a.timestamp))[0] ?? null
  );
}

// ─── Nombre de messages pending ───────────────────────────────────────────
export function countPending(): number {
  return readQueue().filter(i => i.status === 'pending').length;
}

// ─── Marquer comme lu / traité ────────────────────────────────────────────
export function markAsRead(id: string): void {
  writeQueue(readQueue().map(i => (i.id === id ? { ...i, status: 'read' } : i)));
}

export function markAsDone(id: string): void {
  writeQueue(readQueue().map(i => (i.id === id ? { ...i, status: 'done' } : i)));
}
